import { appendAmendment, appendEvent, verifyChain } from "./chain";
import { bcCategorySuggest } from "./bc";
import { buildBcPrintHtml, buildExportHtml } from "./exportDocument";
import { blankDocumentation, buildSeedIncidents } from "./fixtures";
import { suggestedNotifications } from "./notifications";
import {
  AlertIncidentSeed,
  DismissalReport,
  DocumentationSave,
  ExportPayload,
  FalseAlertInput,
  Incident,
  IncidentActor,
  IncidentAmendment,
  IncidentListQuery,
  VerifyResult
} from "./types";
import { applyLateFlag, validateFinalize } from "./validation";

const STORAGE_KEY = "neuravue.incidentFixtures.v1";
const CLOSED_ALERT_MESSAGE = "This alert was closed as not an incident. Confirm it again to document it.";

function reopenClosedFixture(incident: Incident, actor: IncidentActor): Incident {
  const at = new Date().toISOString();
  incident.confirmed_by = actor.user_id;
  incident.confirmed_by_name = actor.name;
  incident.confirmed_at = at;
  incident.opened_by = incident.opened_by || actor.name;
  incident.opened_at = incident.opened_at || at;
  incident.status = "in_documentation";
  incident.locked_at = undefined;
  incident.locked_by = undefined;
  incident.locked_by_name = undefined;
  incident.documentation = { ...blankDocumentation(incident), ...incident.documentation };
  if (!incident.notifications.length) incident.notifications = suggestedNotifications(incident);
  appendEvent(incident, {
    id: uid("evt"),
    type: "alert_confirmed",
    actor_user_id: actor.user_id,
    actor_name: actor.name,
    at,
    payload: { note: "Staff confirmed this closed alert is real. Documentation started. Nothing was submitted." }
  });
  writeStore();
  return clone(incident);
}

export class IncidentApiError extends Error {
  status: number;
  errors?: string[];
  msgCode?: string;

  constructor(message: string, status: number, errors?: string[], msgCode?: string) {
    super(message);
    this.name = "IncidentApiError";
    this.status = status;
    this.errors = errors;
    this.msgCode = msgCode;
  }
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

let memory: Incident[] | null = null;

function readStore(): Incident[] {
  if (memory) return memory;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      memory = JSON.parse(raw) as Incident[];
      return memory;
    }
  } catch {
    /* session storage can be unavailable */
  }
  memory = buildSeedIncidents(new Date());
  return memory;
}

function writeStore(): void {
  if (!memory) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
  } catch {
    /* ignore quota errors */
  }
}

export function resetIncidentFixtures(now = new Date()): void {
  memory = buildSeedIncidents(now);
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  writeStore();
}

function mustGet(id: string): Incident {
  const found = readStore().find((incident) => incident.id === id);
  if (!found) throw new IncidentApiError("Incident not found.", 404);
  return found;
}

function matches(incident: Incident, query: IncidentListQuery): boolean {
  if (query.status && query.status !== "all") {
    const awaiting =
      !incident.confirmed_at &&
      incident.status !== "false_alert_pending_confirm" &&
      incident.status !== "submitted_locked" &&
      incident.status !== "closed";
    const pendingQuery = query.status === "pending_review" || query.status === "awaiting_staff_review";
    if (pendingQuery) {
      if (!awaiting) return false;
    } else if (query.status === "in_documentation") {
      if (incident.status !== "in_documentation" || !incident.confirmed_at) return false;
    } else if (incident.status !== query.status) {
      return false;
    }
  }
  if (query.type && query.type !== "all" && incident.type !== query.type) return false;
  if (query.facility && query.facility !== "all" && incident.facility_id !== query.facility) return false;
  if (query.alert_id && incident.alert_id !== query.alert_id) return false;
  const when = new Date(incident.alert_at).getTime();
  if (query.date_from && when < new Date(query.date_from).getTime()) return false;
  if (query.date_to) {
    const end = new Date(query.date_to);
    end.setHours(23, 59, 59, 999);
    if (when > end.getTime()) return false;
  }
  return true;
}

function assertUnlocked(incident: Incident): void {
  if (!incident.confirmed_at) {
    throw new IncidentApiError("Confirm the alert before editing documentation.", 409);
  }
  if (incident.locked_at || incident.status === "submitted_locked" || incident.status === "closed") {
    throw new IncidentApiError("This record is locked. Add an amendment. The original stays visible.", 409);
  }
  if (incident.status === "false_alert_pending_confirm") {
    throw new IncidentApiError("This dismissal is locked until a supervisor confirms or rejects it.", 409);
  }
}

function countBy<T extends string>(values: T[]): { key: T; count: number }[] {
  const map = new Map<T, number>();
  values.forEach((value) => map.set(value, (map.get(value) || 0) + 1));
  return Array.from(map.entries()).map(([key, count]) => ({ key, count }));
}

export const fixtureApi = {
  async list(query: IncidentListQuery = {}): Promise<Incident[]> {
    return readStore()
      .filter((incident) => matches(incident, query))
      .sort((a, b) => b.alert_at.localeCompare(a.alert_at))
      .map((incident) => clone(incident));
  },

  async get(id: string): Promise<Incident> {
    return clone(mustGet(id));
  },

  async fromAlert(alertId: string): Promise<Incident> {
    const existing = readStore().find((incident) => incident.alert_id === alertId);
    if (!existing) throw new IncidentApiError("Confirm the alert before a record is created.", 404);
    return clone(existing);
  },

  async confirmFromAlert(
    alertId: string,
    input: { reconfirm?: boolean; threatStatus?: string; seed?: AlertIncidentSeed; actor?: IncidentActor } = {}
  ): Promise<Incident> {
    const reconfirm = input.reconfirm === true;
    const actor = input.actor || { user_id: "staff", name: "Staff member" };
    const existing = readStore().find((incident) => incident.alert_id === alertId);
    if (existing) {
      if (existing.status === "submitted_locked" || (existing.status === "closed" && !existing.false_alert)) {
        throw new IncidentApiError("This record is locked.", 409);
      }
      if (existing.status === "false_alert_pending_confirm" || (existing.status === "closed" && existing.false_alert)) {
        if (!reconfirm) throw new IncidentApiError(CLOSED_ALERT_MESSAGE, 409);
        return reopenClosedFixture(existing, actor);
      }
      if (existing.confirmed_at && existing.status === "in_documentation") return clone(existing);
      if (input.threatStatus === "Resolved" && !reconfirm) throw new IncidentApiError(CLOSED_ALERT_MESSAGE, 409);
      return this.confirm(existing.id, actor);
    }
    if (input.threatStatus === "Resolved" && !reconfirm) throw new IncidentApiError(CLOSED_ALERT_MESSAGE, 409);
    const seed = input.seed;
    const now = new Date().toISOString();
    const kind =
      seed?.type === "unsafe_interaction" || seed?.type === "responsive_behaviour"
        ? "Responsive Behaviour / Abuse"
        : seed?.type || "alert";
    const created: Incident = {
      id: `NV-INC-LOCAL-${readStore().length + 1}`,
      facility_id: seed?.facility_id || "unknown-home",
      facility_name: seed?.facility_name || "This home",
      alert_id: alertId,
      type: seed?.type || "other",
      status: "pending_review",
      province: seed?.province || "other",
      room: seed?.room,
      unit: seed?.unit,
      location: seed?.location,
      camera_id: seed?.camera_id,
      created_at: now,
      alert_at: seed?.alert_at || now,
      alert_summary: `NeuraVue flagged a ${kind} alert at ${
        seed?.location || "the camera location"
      }. The alert was sent to the assigned staff. This is not a finding.`,
      alert_subtype: seed?.alert_subtype,
      notified_staff: [],
      documentation: {},
      reasonable_grounds_decision: { decision: null },
      notifications: [],
      events: [],
      amendments: [],
      model_version: "not-provided"
    };
    appendEvent(created, {
      id: uid("evt"),
      type: "alert_created",
      actor_user_id: "system",
      actor_name: "NeuraVue",
      at: created.alert_at,
      payload: { note: "Alert recorded. Documentation has not started." }
    });
    readStore().unshift(created);
    writeStore();
    return this.confirm(created.id, actor);
  },

  async confirm(id: string, actor: IncidentActor): Promise<Incident> {
    const incident = mustGet(id);
    if (incident.confirmed_at) return clone(incident);
    const at = new Date().toISOString();
    incident.confirmed_by = actor.user_id;
    incident.confirmed_by_name = actor.name;
    incident.confirmed_at = at;
    incident.opened_by = incident.opened_by || actor.name;
    incident.opened_at = incident.opened_at || at;
    incident.status = "in_documentation";
    incident.documentation = { ...blankDocumentation(incident), ...incident.documentation };
    if (!incident.notifications.length) incident.notifications = suggestedNotifications(incident);
    appendEvent(incident, {
      id: uid("evt"),
      type: "alert_confirmed",
      actor_user_id: actor.user_id,
      actor_name: actor.name,
      at,
      payload: { note: "Staff confirmed the alert. Documentation started. Nothing was submitted." }
    });
    writeStore();
    return clone(incident);
  },

  async saveDocumentation(id: string, save: DocumentationSave): Promise<Incident> {
    const incident = mustGet(id);
    assertUnlocked(incident);
    const at = new Date().toISOString();
    if (incident.site_jurisdiction?.residentNamesAllowed === false) {
      const doc = save.documentation || {};
      if ((doc.resident_name || "").trim() || (doc.other_resident_name || "").trim()) {
        throw new IncidentApiError(
          "This home does not store resident names. Enter the room and the resident identifier only.",
          422
        );
      }
    }
    if (save.province && incident.site_jurisdiction?.province && incident.site_jurisdiction.province !== "other") {
      if (save.province !== incident.site_jurisdiction.province) {
        throw new IncidentApiError(
          `This home is ${incident.site_jurisdiction.province}. Confirm that province. A facility admin changes the home. You cannot replace it on this record.`,
          422
        );
      }
    }
    if (save.province_confirmed !== undefined) incident.province_confirmed = Boolean(save.province_confirmed);
    incident.documentation = applyLateFlag(save.documentation, new Date(at));
    if (incident.province === "BC") {
      const suggestion = bcCategorySuggest(incident, incident.documentation);
      incident.documentation.bc_category_suggested = suggestion.category;
      incident.documentation.bc_regulatory_term = suggestion.regulatory_term;
      incident.documentation.bc_product_label = suggestion.product_label;
      incident.documentation.bc_reportable = suggestion.reportable;
    }
    incident.notifications = save.notifications;
    const decision = save.reasonable_grounds_decision;
    const changed = decision.decision && decision.decision !== incident.reasonable_grounds_decision.decision;
    if (changed) {
      incident.reasonable_grounds_decision = {
        ...decision,
        decided_by: save.actor.user_id,
        decided_by_name: save.actor.name,
        decided_at: at
      };
      appendEvent(incident, {
        id: uid("evt"),
        type: "reasonable_grounds_recorded",
        actor_user_id: save.actor.user_id,
        actor_name: save.actor.name,
        at,
        payload: { decision: decision.decision || "" }
      });
    } else {
      incident.reasonable_grounds_decision = { ...incident.reasonable_grounds_decision, ...decision };
    }
    appendEvent(incident, {
      id: uid("evt"),
      type: "documentation_draft_saved",
      actor_user_id: save.actor.user_id,
      actor_name: save.actor.name,
      at
    });
    if (incident.status === "awaiting_staff_review" || incident.status === "open") {
      incident.status = "in_documentation";
    }
    writeStore();
    return clone(incident);
  },

  async submit(id: string, actor: IncidentActor): Promise<Incident> {
    const incident = mustGet(id);
    assertUnlocked(incident);
    const errors = validateFinalize(incident, new Date(), actor);
    if (errors.length) throw new IncidentApiError(errors[0], 422, errors);
    const at = new Date().toISOString();
    incident.documentation.attestation_at = at;
    incident.documentation.attestation_user_id = actor.user_id;
    incident.documentation.late_entry = applyLateFlag(incident.documentation, new Date(at)).late_entry;
    incident.status = "submitted_locked";
    incident.locked_at = at;
    incident.locked_by = actor.user_id;
    incident.locked_by_name = actor.name;
    appendEvent(incident, {
      id: uid("evt"),
      type: "documentation_submitted",
      actor_user_id: actor.user_id,
      actor_name: actor.name,
      at,
      payload: { note: "Record finalized and locked in NeuraVue. Not sent to a regulator." }
    });
    writeStore();
    return clone(incident);
  },

  async amend(
    id: string,
    input: { reason: string; changes: IncidentAmendment["changes"]; actor: IncidentActor }
  ): Promise<Incident> {
    const incident = mustGet(id);
    if (!incident.locked_at && incident.status !== "submitted_locked" && incident.status !== "closed") {
      throw new IncidentApiError("Amendments are added after the record is locked.", 409);
    }
    if (!input.reason.trim() || !input.changes.length) {
      throw new IncidentApiError("An amendment needs a reason and at least one change.", 422);
    }
    const at = new Date().toISOString();
    appendAmendment(incident, {
      id: uid("amd"),
      incident_id: incident.id,
      author_user_id: input.actor.user_id,
      author_name: input.actor.name,
      at,
      reason: input.reason.trim(),
      changes: input.changes
    });
    appendEvent(incident, {
      id: uid("evt"),
      type: "amendment_added",
      actor_user_id: input.actor.user_id,
      actor_name: input.actor.name,
      at,
      payload: { reason: input.reason.trim() }
    });
    writeStore();
    return clone(incident);
  },

  async falseAlert(
    id: string,
    input: FalseAlertInput
  ): Promise<{ incident: Incident; converted: boolean; message: string }> {
    const incident = mustGet(id);
    if (
      incident.status === "submitted_locked" ||
      incident.status === "closed" ||
      incident.status === "false_alert_pending_confirm"
    ) {
      throw new IncidentApiError("This record is locked.", 409);
    }
    const at = new Date().toISOString();
    const convert = input.risk_situation === "yes" || input.concern === "yes" || input.concern === "unsure";
    if (convert) {
      if (!incident.confirmed_at) {
        incident.confirmed_by = input.actor.user_id;
        incident.confirmed_by_name = input.actor.name;
        incident.confirmed_at = at;
        incident.documentation = { ...blankDocumentation(incident), ...incident.documentation };
        if (!incident.notifications.length) incident.notifications = suggestedNotifications(incident);
      }
      incident.status = "in_documentation";
      incident.locked_at = undefined;
      incident.false_alert = undefined;
      appendEvent(incident, {
        id: uid("evt"),
        type: "escalated",
        actor_user_id: input.actor.user_id,
        actor_name: input.actor.name,
        at,
        payload: { note: "A yes on the safety check keeps this as an incident. Nothing was submitted." }
      });
      writeStore();
      return {
        incident: clone(incident),
        converted: true,
        message: "This stays an incident. Complete the documentation. Nothing was submitted."
      };
    }
    if (input.reason_code === "other" && !input.note?.trim()) {
      throw new IncidentApiError("Other needs a short factual note.", 422);
    }
    if (incident.type === "unsafe_interaction" && !input.note?.trim()) {
      throw new IncidentApiError("A Responsive Behaviour / Abuse dismissal needs a factual note.", 422);
    }
    incident.false_alert = {
      reason_code: input.reason_code,
      note: input.note?.trim(),
      risk_situation: input.risk_situation,
      concern: input.concern,
      dismissed_by: input.actor.user_id,
      dismissed_by_name: input.actor.name,
      dismissed_at: at
    };
    incident.documentation = {};
    incident.confirmed_at = undefined;
    incident.confirmed_by = undefined;
    incident.locked_at = at;
    incident.locked_by = input.actor.user_id;
    incident.locked_by_name = input.actor.name;
    incident.status = incident.type === "unsafe_interaction" ? "false_alert_pending_confirm" : "closed";
    appendEvent(incident, {
      id: uid("evt"),
      type: "false_alert_marked",
      actor_user_id: input.actor.user_id,
      actor_name: input.actor.name,
      at,
      payload: { reason_code: input.reason_code }
    });
    if (incident.status === "closed") {
      appendEvent(incident, {
        id: uid("evt"),
        type: "closed",
        actor_user_id: input.actor.user_id,
        actor_name: input.actor.name,
        at,
        payload: { note: "Minimal dismissal record locked. Not sent anywhere." }
      });
    }
    writeStore();
    return {
      incident: clone(incident),
      converted: false,
      message:
        incident.status === "false_alert_pending_confirm"
          ? "Dismissal is waiting for a different supervisor. Anyone can still escalate."
          : "Dismissal saved as a minimal locked record."
    };
  },

  async confirmFalseAlert(
    id: string,
    input: { decision: "confirm" | "reject"; note?: string; actor: IncidentActor }
  ): Promise<Incident> {
    const incident = mustGet(id);
    if (incident.status !== "false_alert_pending_confirm" || !incident.false_alert) {
      throw new IncidentApiError("This record is not waiting for confirmation.", 409);
    }
    if (input.actor.user_id === incident.false_alert.dismissed_by) {
      throw new IncidentApiError("A different staff member must confirm this dismissal.", 403);
    }
    const at = new Date().toISOString();
    incident.false_alert.confirmation = {
      by: input.actor.user_id,
      by_name: input.actor.name,
      role: input.actor.designation || "Supervisor",
      at,
      decision: input.decision,
      note: input.note?.trim()
    };
    appendEvent(incident, {
      id: uid("evt"),
      type: "false_alert_confirmed",
      actor_user_id: input.actor.user_id,
      actor_name: input.actor.name,
      at,
      payload: { decision: input.decision, note: input.note || "" }
    });
    if (input.decision === "reject") {
      incident.confirmed_by = input.actor.user_id;
      incident.confirmed_by_name = input.actor.name;
      incident.confirmed_at = at;
      incident.status = "in_documentation";
      incident.locked_at = undefined;
      incident.locked_by = undefined;
      incident.documentation = { ...blankDocumentation(incident), ...incident.documentation };
      if (!incident.notifications.length) incident.notifications = suggestedNotifications(incident);
      appendEvent(incident, {
        id: uid("evt"),
        type: "alert_confirmed",
        actor_user_id: input.actor.user_id,
        actor_name: input.actor.name,
        at,
        payload: { note: "Dismissal rejected. Documentation is open. The dismissal note stays on the timeline." }
      });
    } else {
      incident.status = "closed";
      appendEvent(incident, {
        id: uid("evt"),
        type: "closed",
        actor_user_id: input.actor.user_id,
        actor_name: input.actor.name,
        at
      });
    }
    writeStore();
    return clone(incident);
  },

  async escalate(id: string, actor: IncidentActor, note: string): Promise<Incident> {
    const incident = mustGet(id);
    const at = new Date().toISOString();
    if (!incident.confirmed_at) {
      incident.confirmed_by = actor.user_id;
      incident.confirmed_by_name = actor.name;
      incident.confirmed_at = at;
      incident.documentation = { ...blankDocumentation(incident), ...incident.documentation };
      if (!incident.notifications.length) incident.notifications = suggestedNotifications(incident);
    }
    if (incident.status !== "submitted_locked") {
      incident.status = "in_documentation";
      incident.locked_at = undefined;
      incident.locked_by = undefined;
    }
    appendEvent(incident, {
      id: uid("evt"),
      type: "escalated",
      actor_user_id: actor.user_id,
      actor_name: actor.name,
      at,
      payload: { note: note || "Escalated. A supervisor cannot block this." }
    });
    writeStore();
    return clone(incident);
  },

  async review(id: string, actor: IncidentActor, note: string, designation: string): Promise<Incident> {
    const incident = mustGet(id);
    if (!incident.locked_at && incident.status !== "submitted_locked") {
      throw new IncidentApiError("Manager review is recorded after the nurse locks the record.", 409);
    }
    const at = new Date().toISOString();
    incident.documentation.supervisor_review = {
      by: actor.user_id,
      name: actor.name,
      designation: designation || actor.designation || "Director of care",
      at,
      note: note.trim()
    };
    appendEvent(incident, {
      id: uid("evt"),
      type: "supervisor_reviewed",
      actor_user_id: actor.user_id,
      actor_name: actor.name,
      at,
      payload: { note: note.trim(), designation }
    });
    writeStore();
    return clone(incident);
  },

  async close(id: string, actor: IncidentActor): Promise<Incident> {
    const incident = mustGet(id);
    if (incident.status !== "submitted_locked" && incident.status !== "closed") {
      throw new IncidentApiError("Close the record after it is finalized.", 409);
    }
    incident.status = "closed";
    appendEvent(incident, {
      id: uid("evt"),
      type: "closed",
      actor_user_id: actor.user_id,
      actor_name: actor.name,
      at: new Date().toISOString()
    });
    writeStore();
    return clone(incident);
  },

  async export(id: string, format?: "html" | "bc" | "pdf"): Promise<ExportPayload> {
    const incident = mustGet(id);
    if ((format === "bc" || format === "pdf") && incident.province !== "BC") {
      throw new IncidentApiError("This record is not a British Columbia record.", 422);
    }
    const html = format === "bc" || format === "pdf" ? buildBcPrintHtml(incident) : buildExportHtml(incident);
    return { incident: clone(incident), html };
  },

  async verify(id: string): Promise<VerifyResult> {
    const incident = mustGet(id);
    return { valid: verifyChain(incident), hash: incident.hash, algorithm: "fixture-chain" };
  },

  async dismissalReport(month: string, facility?: string): Promise<DismissalReport> {
    const rows = readStore().filter((incident) => {
      if (!incident.false_alert) return false;
      if (facility && facility !== "all" && incident.facility_id !== facility) return false;
      return incident.false_alert.dismissed_at.slice(0, 7) === month;
    });
    return {
      month,
      total: rows.length,
      by_reason: countBy(rows.map((row) => row.false_alert!.reason_code)).map((item) => ({
        reason: item.key,
        count: item.count
      })),
      by_type: countBy(rows.map((row) => row.type)).map((item) => ({ type: item.key, count: item.count })),
      by_room: countBy(rows.map((row) => row.room || "Unknown")).map((item) => ({
        room: item.key,
        count: item.count
      })),
      unsafe_interaction_dismissed: rows.filter((row) => row.type === "unsafe_interaction").length,
      pending_confirmation: rows.filter((row) => row.status === "false_alert_pending_confirm").length,
      rows: rows.map((row) => ({
        id: row.id,
        facility_name: row.facility_name,
        type: row.type,
        room: row.room || "—",
        reason: row.false_alert!.reason_code,
        dismissed_at: row.false_alert!.dismissed_at,
        confirmer: row.false_alert!.confirmation?.by_name,
        status: row.status,
        unsafe_interaction: row.type === "unsafe_interaction"
      }))
    };
  }
};

export function visibleFacilities(incidents: Incident[]): { id: string; name: string }[] {
  const map = new Map<string, string>();
  incidents.forEach((incident) => map.set(incident.facility_id, incident.facility_name));
  return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
}

export function monthKey(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
